
/**
 * Prepares an image for PDF inclusion by loading and processing it
 * with improved error handling and image quality
 */
export const prepareImageForPdf = async (url: string): Promise<HTMLImageElement | null> => {
  return new Promise(async (resolve) => {
    try {
      // Add cache busting to URL to prevent Supabase caching issues
      const cacheBustUrl = url.includes('?') 
        ? `${url}&t=${new Date().getTime()}` 
        : `${url}?t=${new Date().getTime()}`;
      
      // First attempt with standard image loading
      const img = await loadImageWithRetry(cacheBustUrl, 3);
      
      if (img) {
        // Create a canvas to process the image
        const canvas = document.createElement('canvas');
        const ctx = canvas.getContext('2d');
        
        if (!ctx) {
          console.error('Failed to get canvas context');
          resolve(null);
          return;
        }
        
        // Set canvas dimensions to match image with a reasonable limit
        const MAX_DIMENSION = 1500; // Limit to prevent memory issues
        const ratio = img.width / img.height;
        
        let width = Math.min(img.width, MAX_DIMENSION);
        let height = width / ratio;
        
        if (height > MAX_DIMENSION) {
          height = MAX_DIMENSION;
          width = height * ratio;
        }
        
        canvas.width = width;
        canvas.height = height;
        
        // Draw with white background to prevent transparency issues
        ctx.fillStyle = '#ffffff';
        ctx.fillRect(0, 0, canvas.width, canvas.height);
        
        // Use better image rendering quality
        ctx.imageSmoothingEnabled = true;
        ctx.imageSmoothingQuality = 'high';
        
        // Draw the image
        ctx.drawImage(img, 0, 0, canvas.width, canvas.height);
        
        // Create a new image from canvas to ensure proper format
        const processedImg = new Image();
        processedImg.crossOrigin = "Anonymous";
        
        processedImg.onload = () => {
          resolve(processedImg);
        };
        
        processedImg.onerror = () => {
          console.error('Failed to load processed image');
          resolve(null);
        };
        
        // Get data URL with high quality JPEG
        processedImg.src = canvas.toDataURL('image/jpeg', 0.92);
      } else {
        resolve(null);
      }
    } catch (error) {
      console.error('Error preparing image:', error);
      resolve(null);
    }
  });
};

/**
 * Load an image with retry logic for better reliability
 */
export const loadImageWithRetry = (url: string, maxRetries = 3): Promise<HTMLImageElement | null> => {
  return new Promise((resolve) => {
    let retries = 0;
    
    // Function to attempt loading
    const attemptLoad = () => {
      const img = new Image();
      img.crossOrigin = "Anonymous";
      
      img.onload = () => {
        // Check if image actually loaded with proper dimensions
        if (img.width > 0 && img.height > 0) {
          resolve(img);
        } else {
          handleRetry();
        }
      };
      
      img.onerror = () => {
        handleRetry();
      };
      
      // Set a timeout in case the image load hangs
      const timeoutId = setTimeout(() => {
        console.warn(`Image load timeout for ${url}`);
        handleRetry();
      }, 5000);
      
      function handleRetry() {
        clearTimeout(timeoutId);
        
        retries++;
        if (retries < maxRetries) {
          console.warn(`Retrying image load for ${url}, attempt ${retries + 1}/${maxRetries}`);
          // Wait with exponential backoff
          setTimeout(attemptLoad, 300 * Math.pow(2, retries));
        } else {
          console.error(`Failed to load image after ${maxRetries} attempts: ${url}`);
          resolve(null);
        }
      }
      
      // Start loading
      img.src = url;
    };
    
    // Start the first attempt
    attemptLoad();
  });
};

// Type for test mocks that don't require full HTMLImageElement implementation
export interface MockImageForTests {
  width: number;
  height: number;
  onload: () => void;
  onerror: () => void;
  src: string;
  crossOrigin: string;
}
